<template>
    <router-link
        v-if="to"
        :to="to"
        :class="{ 'has-text-grey': isFormer }"
        :title="isFormer ? $t('Former member') : $t('Open the record')"
    >
        {{ display }}
    </router-link>
    <span v-else :class="{ 'has-text-grey': isFormer }" :title="isFormer ? $t('Former member') : null">{{
        display
    }}</span>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useOrgStore } from '@/stores/org';
import { resolveRelation } from '@/utils/relationResolver';

/**
 * Read-only display of a relation custom field: resolves a stored record id
 * (user or contact) into a human-readable name via the shared resolver.
 *
 * Someone who left the organization still renders by name — the record has to
 * stay readable — but muted, so the reader can tell them apart from a colleague
 * they could still assign work to.
 */
const props = defineProps({
    entity: { type: String, required: true }, // 'user' | 'employee_profile' | 'contact' ('employee' alias for builtin fields)
    value: { type: [Number, String], default: null },
    /**
     * Turn the name into a link to the record it names. Off by default: in a
     * list cell the row click owns the interaction and a relation is reached
     * through ListRecordLink's hover arrow instead. A fiche has no such click,
     * so a named record that cannot be opened is a dead end — the reader has to
     * leave and search for it by hand.
     *
     * Only a contact is linked: `user` resolves through the org's employees
     * endpoint, whose id is a platform User, not the Payroll EmployeeProfile
     * that `employee-preview` expects — linking it would open the wrong record.
     */
    link: { type: Boolean, default: false }
});

const orgStore = useOrgStore();
const route = useRoute();
const router = useRouter();
const display = ref('—');
const isFormer = ref(false);

const to = computed(() => {
    if (!props.link || props.entity !== 'contact' || !props.value) {
        return null;
    }
    // An id the resolver could not name is a record that is gone or out of
    // reach; offering to open it is offering a 404.
    if (display.value === '—') {
        return null;
    }
    // The route belongs to the Contacts module; an organization without it has
    // no such screen, and a router-link to an unknown name throws on render.
    if (!router.hasRoute('contact-preview')) {
        return null;
    }

    return { name: 'contact-preview', params: { id: props.value, org: route.params.org } };
});

watch(
    () => [props.entity, props.value],
    async ([entity, value]) => {
        const resolved = await resolveRelation(entity, value, orgStore.api);
        display.value = resolved.label;
        isFormer.value = resolved.isFormer;
    },
    { immediate: true }
);
</script>
